import { Check, Circle, X } from "lucide-react";

// Ordered flow of statuses used to render the tracker
const FLOW = [
  { key: "pending_payment", label: "Placed", desc: "Awaiting payment" },
  { key: "paid", label: "Paid", desc: "Payment confirmed" },
  { key: "shipped", label: "Shipped", desc: "In transit" },
  { key: "delivered", label: "Delivered", desc: "Arrived" },
];

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return ""; }
}

export default function OrderTimeline({ order }) {
  const history = order.status_history || [];
  const byKey = Object.fromEntries(history.map((h) => [h.status, h.at]));
  const currentStatus = order.status;
  const isCancelled = currentStatus === "cancelled";

  if (isCancelled) {
    return (
      <div className="border border-borderSubtle bg-white p-6" data-testid={`timeline-${order.order_number}`}>
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-full bg-oxblood text-bone flex items-center justify-center">
            <X size={14} strokeWidth={2.5}/>
          </span>
          <div>
            <p className="font-serif text-lg text-oxblood">Order cancelled</p>
            <p className="text-xs text-stone font-mono uppercase tracking-widest">{fmt(byKey.cancelled)}</p>
          </div>
        </div>
      </div>
    );
  }

  // Determine index of current step
  const currentIdx = Math.max(0, FLOW.findIndex((s) => s.key === currentStatus));

  return (
    <div className="border border-borderSubtle bg-white p-6 md:p-8" data-testid={`timeline-${order.order_number}`}>
      <p className="overline mb-6">Progress</p>
      <ol className="relative">
        {FLOW.map((step, idx) => {
          const at = byKey[step.key];
          const done = !!at || idx < currentIdx;
          const active = idx === currentIdx;
          const isLast = idx === FLOW.length - 1;
          return (
            <li key={step.key} className="flex gap-4 pb-6 last:pb-0 relative" data-testid={`timeline-step-${step.key}`}>
              {/* connector line */}
              {!isLast && (
                <span
                  aria-hidden
                  className={`absolute left-[15px] top-8 bottom-0 w-px ${done ? "bg-oxblood" : "bg-borderSubtle"}`}
                />
              )}
              {/* dot */}
              <span
                className={`shrink-0 relative z-10 w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  done
                    ? "bg-oxblood border-oxblood text-bone"
                    : active
                    ? "bg-white border-oxblood text-oxblood"
                    : "bg-white border-borderSubtle text-stone"
                }`}
                data-testid={`timeline-dot-${step.key}`}
                data-state={done ? "done" : active ? "active" : "pending"}
              >
                {done ? <Check size={14} strokeWidth={2.5}/> : <Circle size={8} strokeWidth={3} fill={active ? "currentColor" : "none"}/>}
              </span>
              <div className="flex-1 pt-1">
                <p className={`font-serif text-lg leading-tight ${done || active ? "text-charcoal" : "text-stone"}`}>
                  {step.label}
                </p>
                <p className={`text-xs mt-0.5 ${done || active ? "text-stone" : "text-stone/60"}`}>
                  {step.desc}
                </p>
                {at && (
                  <p className="text-[10px] font-mono uppercase tracking-widest text-stone mt-1">
                    {fmt(at)}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
